import React, { useEffect, useState } from 'react';
import { Clock, Heart, Star, MessageSquare, CheckCircle } from 'lucide-react';
import AlertModal from './AlertModal';

interface ReviewComponentProps {
  reviews: any[];
  loading?: boolean;
  emptyText?: string;
  user?: any;

  showHelpful?: boolean;
  onHelpfulClick?: (reviewId: string) => Promise<void> | void;
  renderFooter?: (review: any) => React.ReactNode;
}

const ReviewComponent: React.FC<ReviewComponentProps> = ({
  reviews: reviewsProp,
  loading = false,
  emptyText = 'No reviews yet. Be the first to share your experience!',
  user,
  showHelpful = true,
  onHelpfulClick,
  renderFooter,
}) => {
  const [reviews, setReviews] = useState<any[]>(reviewsProp ?? []);

  // Photo lightbox state
  const [activePhoto, setActivePhoto] = useState<{ url: string; reviewId: string } | null>(null);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-5 h-5 ${i < Math.floor(rating)
          ? 'text-yellow-400 fill-current'
          : 'text-gray-300'
          }`}
      />
    ));
  };

  const handleHelpful = async (reviewId: string) => {
    if (!onHelpfulClick) return;
    if (!user?.id) {
      setModal({
        open: true,
        title: 'Sorry!',
        message: 'Please log in to vote helpful.',
        variant: 'error',
      });
      return;
    }
    try {
      await onHelpfulClick(reviewId);
      // optimistic update: bump helpful count locally
      setReviews(prev =>
        prev.map(r =>
          r.id === reviewId
            ? { ...r, helpful_count: (r.helpful_count || 0) + 1 }
            : r
        )
      );
    } catch (error: any) {
      setModal({
        open: true,
        title: 'Sorry!',
        message: error.message || 'Unable to add helpful vote.',
        variant: 'error',
      });
    }
  };

  // Alert modal state
  const [modal, setModal] = useState({
    open: false,
    title: '',
    message: '',
    variant: 'info' as 'success' | 'error' | 'info',
  });

  useEffect(() => {
    setReviews(reviewsProp ?? []);
  }, [reviewsProp]);

  return (
    <>
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : reviews.length === 0 ? (
        <div className="bg-gray-50 rounded-lg p-8 text-center">
          <p className="text-gray-600">{emptyText}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <div key={review.id} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <span className="font-medium text-gray-900">
                    {review.is_anonymous ? 'Anonymous' : review.user?.name}
                  </span>

                  {/* verify icon */}
                  {review.is_verified ? (
                    <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                      <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  ) : null}
                </div>
                <div className="flex items-center space-x-1 text-sm text-gray-500">
                  <Clock className="w-3 h-3" />
                  <span>{formatDate(review.created_at)}</span>
                </div>
              </div>

              <div className="flex items-center space-x-2 mb-2">
                <div className="flex">
                  {renderStars(review.rating)}
                </div>
              </div>

              <h5 className="font-medium text-gray-900 mb-1">{review.title}</h5>
              <p className="text-gray-700 text-sm mb-3">{review.content}</p>

              {/* review photos */}
              {review.photos?.length > 0 && (
                <div className="mt-3 mb-3 flex flex-wrap gap-3">
                  {review.photos.map((url: string) => (
                    <button
                      key={`btn${review.id}-${url}`}
                      onClick={() => setActivePhoto({ url, reviewId: review.id })}
                      className="focus:outline-none"
                    >
                      <img
                        src={url}
                        alt="Review photo"
                        className="w-20 h-20 object-cover rounded-lg border"
                      />
                    </button>
                  ))}
                </div>
              )}
              {activePhoto && (
                <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6">
                  <button
                    className="absolute top-4 right-4 text-white text-2xl"
                    onClick={() => setActivePhoto(null)}
                  >
                    ×
                  </button>
                  <img
                    src={activePhoto.url}
                    alt="Review photo"
                    className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg"
                  />
                </div>
              )}

              {/* aspects rating */}
              {review.aspects?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3 mb-3">
                  {review.aspects.map((aspect: any) => (
                    <div key={`${review.id}-${aspect.category}`} className="bg-gray-100 rounded-full px-3 py-1 text-xs flex">
                      <span className="capitalize">{String(aspect.category).replace('_', ' ')}</span>
                      <span className="ml-1 flex items-center gap-1">
                        {aspect.rating}
                        <Star className="w-2 h-2 text-yellow-400 fill-current" />
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Business Response */}
              {review.business_response && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <MessageSquare className="w-3.5 h-3.5 text-blue-600" />
                    <span className="text-xs font-semibold text-blue-700">Business Response</span>
                    {review.business_response_date && (
                      <span className="text-xs text-blue-500 ml-auto">
                        {new Date(review.business_response_date).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-blue-800 leading-relaxed">{review.business_response}</p>
                </div>
              )}

              {/* Response status badge (when no response) */}
              {!review.business_response && (
                <div className="mt-2 flex items-center gap-1 text-xs text-gray-400">
                  <MessageSquare className="w-3 h-3" />
                  <span>No business response yet</span>
                </div>
              )}

              {/* Helpful Vote */}
              {(showHelpful || renderFooter) && (
                <div className="mt-3 space-y-3">
                  {showHelpful ? (
                    <button
                      className="flex items-center space-x-1 text-sm text-gray-600 hover:text-gray-800"
                      onClick={() => handleHelpful(review.id)}
                    >
                      <Heart className="w-4 h-4" />
                      <span>Helpful ({review.helpful_count || 0})</span>
                    </button>
                  ) : null}

                  {renderFooter ? <div>{renderFooter(review)}</div> : null}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <AlertModal
        open={modal.open}
        title={modal.title}
        message={modal.message}
        variant={modal.variant}
        onClose={() => setModal(prev => ({ ...prev, open: false }))}
      />
    </>
  );
};

export default ReviewComponent;
